import { useAppDispatch, useAppSelector } from 'app/store';
import { MessageType } from 'types/chat.types';
import { getPlayerHeight } from 'app/store/AudioPlayer/selectors';
import { ReactComponent as CloseIcon } from 'resources/images/close.svg';
import { Flex } from '@chakra-ui/layout';
import { Box, Card, IconButton, VStack, Text } from '@chakra-ui/react';
import { useResizableBlock } from 'app/utils/hooks/useResizableBlock';
import { getSongChatBarSettings } from 'app/store/Settings/selectors';
import { setSongChatBarSettings } from 'app/store/Settings/slice';
import {
  getCreateChatMessageLink,
  getCurrentChannel,
  getShouldUpdateCurrentChannel,
} from 'app/store/Chat/selectors';
import { useFetchValidationSchema } from 'app/utils/hooks';
import { useEffect, useRef, useState } from 'react';
import {
  fetchChatChannelMessagesEffect,
  fetchSongChannelEffect,
} from 'app/store/Chat/effects';
import { VirtuosoHandle } from 'react-virtuoso';
import { removeCurrentChannel } from 'app/store/Chat/slice';
import { getSong } from 'app/store/SongList/selectors';
import { getStyles } from './styles';
import { HEADER_HEIGHT } from '../HeaderBar';
import { MessageForm } from '../Chat/MessageForm';
import { ChatLoader } from '../Chat/ChatLoader/ChatLoader';
import { MessageDeleteDialog } from './MessageDeleteDialog';
import { Messages } from './Messages';

export const SongChatBar = () => {
  const virtuosoRef = useRef<VirtuosoHandle>(null);
  const dispatch = useAppDispatch();
  const playerHeight = useAppSelector(getPlayerHeight);
  const songChatBar = useAppSelector(getSongChatBarSettings);
  const height = `calc(100vh - ${HEADER_HEIGHT}px - ${playerHeight}px)`;

  const {
    panelRef,
    cursor,
    handleMouseDown,
    isDragging,
    forceCompactMode,
  } = useResizableBlock('chat-bar', 'rtl', false);

  const {
    rootSx,
    wrapperSx,
    contentSx,
    cardSx,
    sidebarResizeLineSx,
    closeIconSx,
  } = getStyles({
    forceCompactMode,
    isDragging,
  });

  const currentChannel = useAppSelector(getCurrentChannel);
  const createMessageLink = useAppSelector(getCreateChatMessageLink);
  const { validationSchema } = useFetchValidationSchema({
    link: createMessageLink,
  });
  const shouldUpdateChannel = useAppSelector(getShouldUpdateCurrentChannel);

  const song = useAppSelector(getSong);
  const [deleteMessage, setDeleteMessage] = useState<MessageType>();
  const [loading, setLoading] = useState(true);

  const handleSongChatChannelFetch = async (channelGuid: string) => {
    try {
      await dispatch(fetchSongChannelEffect({ channelGuid }));
    } catch (e: any) {
      // do nothing for now
    }
  };

  useEffect(() => {
    if (song?.chat) {
      handleSongChatChannelFetch(song.chat.guid);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [song?.chat?.guid]);

  useEffect(
    () => {
      if (shouldUpdateChannel && song?.chat) {
        dispatch(
          fetchSongChannelEffect({ channelGuid: song.chat.guid, update: true })
        );
      }
    }, // eslint-disable-next-line react-hooks/exhaustive-deps
    [shouldUpdateChannel]
  );

  useEffect(
    () => () => {
      dispatch(removeCurrentChannel());
    }, // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  useEffect(() => {
    const fetchMessages = async () => {
      try {
        setLoading(true);
        await dispatch(fetchChatChannelMessagesEffect()).unwrap();
      } catch (e) {
        // nothing
      } finally {
        setLoading(false);
      }
    };

    if (currentChannel?._links?.listMessages) {
      fetchMessages();
    }
  }, [dispatch, currentChannel?._links?.listMessages]);

  if (!songChatBar) {
    return null;
  }

  const isLoading = !validationSchema || loading;
  return (
    <Flex ref={panelRef} sx={rootSx}>
      <Flex sx={wrapperSx}>
        <Box
          onMouseDown={handleMouseDown}
          cursor={forceCompactMode ? 'default' : cursor}
          sx={sidebarResizeLineSx}
        >
          <Box />
        </Box>
        <VStack sx={contentSx} height={height} spacing={1}>
          <Card sx={cardSx} data-testid="right-bar-card">
            <Flex
              gap={1}
              alignItems="center"
              justifyContent="space-between"
              flexWrap="wrap"
            >
              <Flex flex={1} alignItems="center" justifyContent="space-between">
                <Text color="textSubdued" fontWeight={500}>
                  Messages
                </Text>
                <IconButton
                  sx={closeIconSx}
                  variant="ghost"
                  icon={<CloseIcon />}
                  aria-label="close-song-chat"
                  onClick={() => {
                    dispatch(setSongChatBarSettings());
                  }}
                />
              </Flex>
            </Flex>

            {isLoading ? (
              <ChatLoader />
            ) : (
              <Messages
                virtuosoRef={virtuosoRef}
                setDeleteMessage={setDeleteMessage}
              />
            )}
            <MessageForm
              channelGuid={currentChannel?.guid}
              messageInputProps={{
                placeholder: 'Message',
              }}
              validation={validationSchema!}
              virtuosoRef={virtuosoRef}
              disabled={isLoading}
            />
          </Card>
        </VStack>
      </Flex>
      <MessageDeleteDialog
        message={deleteMessage}
        onClose={() => setDeleteMessage(undefined)}
      />
    </Flex>
  );
};
